<template>
  <div
    class="signup-overlay"
    :class="{ open: isOpen, 'is-submitting': overlaySubmitting, 'is-done': overlayDone }"
    :data-mode="mode"
    :aria-hidden="isOpen ? 'false' : 'true'"
    @pointerdown="dismissKeyboardOnTapOutside"
    @mousedown="onOverlayMouseDown"
    @mouseup="onOverlayMouseUp"
  >
    <div
      class="signup-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="dialogLabel"
    >
      <div class="signup-head"></div>

      <button
        v-if="isOpen"
        class="signup-close"
        type="button"
        :aria-label="t('close')"
        @click="requestClose"
      >
        <icon-close
          :size="14"
          :stroke-width="2.5"
        />
      </button>

      <!-- Single-view head (login / forgot) — register mode has its own progress dots inside RegisterForm. -->
      <div
        v-if="mode !== 'register'"
        class="auth-single-head"
      >
        <div class="lm-big">
          <icon-heart />
        </div>
      </div>

      <RegisterForm
        v-if="mode === 'register'"
        ref="register"
        :cta-location="ctaLocation"
        @submitting="submitting = $event"
        @submit-done="onRegisterDone"
        @account-created="markCompleted"
      />

      <!-- Login + Forgot share the same body padding via the single-view CSS rules. -->
      <div
        v-else
        class="signup-body"
      >
        <LoginForm
          v-if="mode === 'login'"
          :show-reset-banner="showLoginResetBanner"
          :cta-location="ctaLocation"
          @switch-mode="onLoginSwitchMode"
          @submitting="submitting = $event"
          @login-success="onLoginSuccess"
        />
        <ForgotPasswordForm
          v-if="mode === 'forgot'"
          :initial-email="forgotInitialEmail"
          @switch-mode="setMode"
          @submitting="submitting = $event"
        />
        <ResetPasswordForm
          v-if="mode === 'reset'"
          @switch-mode="setMode"
          @submitting="submitting = $event"
        />
      </div>

      <SubmitLoader v-if="mode === 'register'" />
    </div>

    <CancelConfirmDialog
      :open="confirmCancelOpen"
      @keep="confirmCancelOpen = false"
      @confirm="confirmClose"
    />
  </div>
</template>

<script>
import { i18nMixin } from './helpers';
import RegisterForm from './RegisterForm.vue';
import LoginForm from './LoginForm.vue';
import ForgotPasswordForm from './ForgotPasswordForm.vue';
import ResetPasswordForm from './ResetPasswordForm.vue';
import SubmitLoader from './components/SubmitLoader.vue';
import CancelConfirmDialog from './components/CancelConfirmDialog.vue';
import IconClose from '../icons/IconClose.vue';
import IconHeart from '../icons/IconHeart.vue';
import { resolveCtaMeta, trackEvent } from '../../../analytics/ga4';

const VALID_MODES = ['register', 'login', 'forgot', 'reset'];

export default {
  name: 'SignupModal',
  mixins: [i18nMixin],
  components: {
    RegisterForm,
    LoginForm,
    ForgotPasswordForm,
    ResetPasswordForm,
    SubmitLoader,
    CancelConfirmDialog,
    IconClose,
    IconHeart,
  },
  data() {
    return {
      isOpen: false,
      mode: 'register',
      submitting: false,
      submitDone: false,
      confirmCancelOpen: false,
      showLoginResetBanner: false,
      backdropMouseDown: false,
      // When set, RegisterForm pre-fills from this AuthUserResource payload
      // and skips POST /register (account already exists — we only need
      // PUT /register/update). Triggered by LandingPage when the user lands
      // on the page mid-registration. Also doubles as a "lock" flag: the
      // modal pre-fills RegisterForm and skips POST /register on submit.
      continueUser: null,
      // Stashed by setMode('forgot', { email }) so a USER_SELF_DELETE login
      // failure lands the user on the forgot form with their email already
      // filled in (matches legacy LoginCard.vue handler).
      forgotInitialEmail: '',
      // Scroll offset captured when the body scroll-lock engages, so we can
      // restore the exact position on unlock (see lockScroll/unlockScroll).
      scrollY: 0,
      ctaLocation: 'unknown',
      // True after successful register/login so modal_close is not fired.
      completed: false,
    };
  },
  provide() {
    return {
      signupCtaLocation: () => this.ctaLocation,
    };
  },
  computed: {
    dialogLabel() {
      if (this.mode === 'login') {
        return `${this.t('login_title_prefix')}${this.t('login_title_grad')}${this.t(
          'login_title_suffix'
        )}`;
      }
      if (this.mode === 'forgot') {
        return `${this.t('forgot_title_prefix')}${this.t('forgot_title_grad')}${this.t(
          'forgot_title_suffix'
        )}`;
      }
      if (this.mode === 'reset') {
        return `${this.t('reset_title_prefix')}${this.t('reset_title_grad')}${this.t(
          'reset_title_suffix'
        )}`;
      }
      return this.t('step1_eyebrow');
    },
    overlaySubmitting() {
      return this.mode === 'register' && this.submitting;
    },
    overlayDone() {
      return this.mode === 'register' && this.submitDone;
    },
  },
  mounted() {
    this.$eventHub.$on('viluu-landing:open-signup', this.handleOpen);
    document.addEventListener('keydown', this.onKeydown);
    // [data-open-signup] handler so we don't have to rewire every CTA when
    // dropping new buttons into CMS/Blade content later.
    document.addEventListener('click', this.handleDocClick, true);
    window.addEventListener('hashchange', this.maybeOpenFromHash);
    this.maybeOpenFromHash();
  },
  beforeDestroy() {
    this.$eventHub.$off('viluu-landing:open-signup', this.handleOpen);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.handleDocClick, true);
    window.removeEventListener('hashchange', this.maybeOpenFromHash);
    this.unlockScroll();
  },
  methods: {
    handleOpen({ mode, continueUser, cta_location } = {}) {
      this.open(mode, continueUser, cta_location || null);
    },
    onLoginSwitchMode(mode, ctx = {}) {
      if (mode === 'register') {
        this.open('register', null, 'login_modal');
        return;
      }
      this.setMode(mode, ctx);
    },
    onOverlayMouseDown(e) {
      this.backdropMouseDown = e.target === e.currentTarget;
    },
    // Tap on a non-interactive area while an input is focused → blur it, which
    // dismisses the iOS soft keyboard. Cheaper and more reliable than resizing
    // the modal around the keyboard.
    dismissKeyboardOnTapOutside(e) {
      const active = document.activeElement;
      if (!active || !/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) {
        return;
      }
      // Bail when the tap targets anything interactive, because blurring on
      // pointerdown fights the element's own behaviour:
      //   - another field / contenteditable → let focus move there;
      //   - button / link → blurring first reflows the page and the click
      //     misses, forcing a second tap to submit.
      // For those, the keyboard still closes naturally (focus moves, or the
      // step changes). Labels are deliberately NOT excluded: signup fields
      // associate them via aria-labelledby rather than `for`, so a label tap
      // no longer refocuses the input — it can just dismiss the keyboard.
      const target = e.target;
      if (
        target &&
        target.closest &&
        target.closest('input, textarea, select, [contenteditable], button, a, [role="button"]')
      ) {
        return;
      }
      active.blur();
    },
    onOverlayMouseUp(e) {
      const startedAndEndedOnBackdrop = this.backdropMouseDown && e.target === e.currentTarget;
      this.backdropMouseDown = false;
      if (startedAndEndedOnBackdrop) {
        this.requestClose();
      }
    },
    handleDocClick(e) {
      const trigger = e.target && e.target.closest && e.target.closest('[data-open-signup]');
      if (!trigger) {
        return;
      }
      e.preventDefault();
      const meta = resolveCtaMeta(trigger, { cta_location: 'unknown' });
      this.open(trigger.getAttribute('data-auth-mode') || 'register', null, meta.cta_location);
    },
    maybeOpenFromHash() {
      const h = (location.hash || '').toLowerCase();
      if (h === '#registrieren' || h === '#register') {
        this.open('register');
      } else if (h === '#einloggen' || h === '#login') {
        this.open('login', null, 'header');
      }
    },
    open(mode, continueUser = null, ctaLocation = null) {
      // Continue-mode is always 'register': we're resuming a partly-filled
      // account, so login/forgot don't apply.
      if (continueUser) {
        this.mode = 'register';
      } else {
        this.mode = VALID_MODES.includes(mode) ? mode : 'register';
      }
      this.continueUser = continueUser || null;
      this.submitting = false;
      this.submitDone = false;
      this.completed = false;
      this.showLoginResetBanner = false;
      if (ctaLocation) {
        this.ctaLocation = ctaLocation;
      } else if (!this.isOpen) {
        this.ctaLocation = this.mode === 'login' ? 'header' : 'unknown';
      }
      // RegisterForm keeps its own state across open/close cycles (we don't
      // re-mount it). Force a reset so users don't see last session's data.
      this.$nextTick(() => {
        const reg = this.$refs.register;
        if (!reg) {
          return;
        }
        if (continueUser && typeof reg.continueWithUser === 'function') {
          reg.continueWithUser(continueUser);
        } else if (typeof reg.reset === 'function') {
          reg.reset();
        }
      });
      const wasOpen = this.isOpen;
      this.isOpen = true;
      this.lockScroll();
      if (!wasOpen && !continueUser) {
        if (this.mode === 'register') {
          trackEvent('sign_up_start', { cta_location: this.ctaLocation });
        } else if (this.mode === 'login') {
          trackEvent('login_modal_open', { cta_location: this.ctaLocation || 'header' });
        }
      }
    },
    setMode(mode, ctx = {}) {
      if (!VALID_MODES.includes(mode)) {
        return;
      }
      this.mode = mode;
      this.submitting = false;
      this.forgotInitialEmail = mode === 'forgot' && ctx && ctx.email ? ctx.email : '';
      // ResetPasswordForm bounces here after a successful reset; flag the login
      // form so it shows the "password updated" banner.
      this.showLoginResetBanner = mode === 'login' && !!(ctx && ctx.afterReset);
    },
    requestClose() {
      if (this.submitting) {
        return;
      }
      const registerHasInput =
        this.mode === 'register' && this.$refs.register && this.$refs.register.hasUserInput;
      if (this.continueUser || registerHasInput) {
        this.confirmCancelOpen = true;
      } else {
        this.close();
      }
    },
    async abandonContinueSession() {
      try {
        await axios.post(zRoute('logout'));
      } catch (e) {
        // Still reset the UI so the user isn't stuck on the landing.
      }
      this.continueUser = null;
      const reg = this.$refs.register;
      if (reg && typeof reg.reset === 'function') {
        reg.reset();
      }
      this.close();
    },
    confirmClose() {
      this.confirmCancelOpen = false;
      if (this.continueUser) {
        this.abandonContinueSession();
      } else {
        this.close();
      }
    },
    close() {
      if (this.isOpen && !this.completed) {
        const params = {
          modal_type: this.mode === 'login' || this.mode === 'forgot' || this.mode === 'reset'
            ? 'login'
            : 'sign_up',
        };
        if (params.modal_type === 'sign_up' && this.$refs.register) {
          params.last_step = this.$refs.register.step || 1;
        }
        trackEvent('modal_close', params);
      }
      this.continueUser = null;
      this.isOpen = false;
      this.unlockScroll();
    },
    markCompleted() {
      this.completed = true;
    },
    onRegisterDone() {
      this.submitDone = true;
      this.markCompleted();
    },
    onLoginSuccess() {
      this.markCompleted();
    },
    // iOS Safari ignores `overflow: hidden` on <body> for touch scrolling, and
    // worse, focusing an input inside the `position: fixed` overlay makes it
    // scroll the document to reveal the field — which visually jerks the whole
    // modal ("the jump"). Pinning the body with `position: fixed` at a negative
    // top genuinely freezes it; we restore the scroll position on unlock.
    lockScroll() {
      this.scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.scrollY}px`;
      document.body.classList.add('signup-locked');
    },
    unlockScroll() {
      if (!document.body.classList.contains('signup-locked')) {
        return;
      }
      document.body.classList.remove('signup-locked');
      document.body.style.top = '';
      // The landing sets `scroll-behavior: smooth` on <html> (_landing-v2.scss),
      // which would make this restore animate from the top down through every
      // section. Force an instant jump back to the saved position.
      window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen && !this.confirmCancelOpen) {
        this.requestClose();
      }
    },
  },
};
</script>
